import type { Logger } from '@core/model/domain/logger';
import type { TaggingConstantData, TaggingInitData } from '@core/model/domain/taggingTracker';
import {
  ClickMetric,
  Datalayer,
  ErrorMetric,
  PageMetric,
  TaggingContainer,
  TaggingTracker,
} from '@core/model/domain/taggingTracker';
import { promiseWithTimeout } from '@core/model/framework/promiseWithTimeout';
import { SERVICE_ID } from '@di/serviceId';
import { environment } from '@environments/environment';
import { inject, injectable } from 'inversify';
import type { TagCommanderService } from 'ngx-tag-commander';
import { Subject, concatMap, delay, of, tap } from 'rxjs';

type SendTagEvent = {
  type: 'click' | 'page';
  datalayer: Datalayer | { click: string };
};

@injectable()
export class TagCommanderTracker implements TaggingTracker {
  private isInitialized: boolean;
  private taggingConstantData!: TaggingConstantData;
  private sendTagEvent$: Subject<SendTagEvent>;
  private previousPage!: string;

  constructor(
    private readonly tagCommander: TagCommanderService,
    private readonly taggingContainers: Array<TaggingContainer>,
    @inject(SERVICE_ID.logger) private readonly logger: Logger
  ) {
    this.isInitialized = false;

    this.sendTagEvent$ = new Subject<SendTagEvent>();

    /*
      For some reason, click event may be sent before page event, even when page event has been triggered earlier. Empirically, it has been defined 
      that waiting 500ms between each container reload solves the issue.

      See :
      https://community.commandersact.com/tagcommander/user-manual/container-management/tags#setting-a-timeout-on-a-tag
      https://community.commandersact.com/tagcommander/tips-and-tricks/best-practices/common-trigger-strategies#click-navigates-to-an-internal-page
    */
    this.sendTagEvent$
      .pipe(
        concatMap((event) =>
          of(event).pipe(
            tap((event) => {
              if (event.type === 'page') {
                this.tagCommander.setTcVars(event.datalayer);
                this.tagCommander.reloadAllContainers({ events: { page: [{}, event.datalayer] } });
              } else {
                this.tagCommander.triggerEvent('click', {}, event.datalayer);
              }
            }),
            delay(500)
          )
        )
      )
      .subscribe();
  }

  private async initialize(initData: TaggingInitData): Promise<void> {
    if (this.isInitialized) {
      throw new Error('TagCommander is already initialized');
    }

    const env_work = environment.env === 'prod' ? 'prod' : 'qlf';
    const page_language = 'fr';

    this.taggingConstantData = { ...initData, env_work, page_language };

    const datalayer: Datalayer = {
      ...this.taggingConstantData,
      error_page: '',
      error_type: '',
      error_category: '',
      click: '',
      page: '',
      page_chapter1: '',
      page_chapter2: '',
      page_chapter3: '',
      page_type: '',
    };

    window['tc_vars'] = [];
    this.tagCommander.setTcVars(datalayer);
    this.tagCommander.setDebug(!environment.production);

    const areContainersLoaded = await this.addContainers(this.taggingContainers);

    if (areContainersLoaded) {
      this.isInitialized = true;
      this.logger.logEvent('TagCommander - initialized');
    }
  }

  async ensureInitialization(dataFromCaller: TaggingInitData): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize(dataFromCaller);
    }

    return this.isInitialized;
  }

  capturePageEvent(pageMetric: PageMetric): void {
    this.throwIfNotInitialized();

    if (this.previousPage === pageMetric.page_chapter3) {
      return;
    }

    const datalayer: Datalayer = {
      ...this.taggingConstantData,
      ...pageMetric,
      click: '',
      error_page: '',
      error_category: '',
      error_type: '',
    };

    this.sendTagEvent$.next({
      type: 'page',
      datalayer,
    });

    this.previousPage = datalayer.page_chapter3;
  }

  captureClickEvent(clickMetric: ClickMetric, pageMetric: PageMetric): void {
    this.throwIfNotInitialized();

    if (this.previousPage !== pageMetric.page_chapter3) {
      const datalayer: Datalayer = {
        ...this.taggingConstantData,
        ...pageMetric,
        ...clickMetric,
        error_page: '',
        error_category: '',
        error_type: '',
      };

      this.sendTagEvent$.next({
        type: 'page',
        datalayer,
      });

      this.previousPage = datalayer.page_chapter3;
    }

    this.sendTagEvent$.next({
      type: 'click',
      datalayer: clickMetric,
    });
  }

  captureErrorEvent(errorMetric: ErrorMetric, pageMetric: PageMetric): void {
    this.throwIfNotInitialized();

    if (this.previousPage === pageMetric.page_chapter3) {
      return;
    }

    const datalayer: Datalayer = {
      ...this.taggingConstantData,
      ...pageMetric,
      ...errorMetric,
      click: '',
    };

    this.sendTagEvent$.next({
      type: 'page',
      datalayer,
    });

    this.previousPage = pageMetric.page_chapter3;
  }

  private async addContainers(containers: Array<TaggingContainer>): Promise<boolean> {
    try {
      await promiseWithTimeout(
        Promise.all(containers.map((container) => this.addContainer(container))),
        3000
      );
      return true;
    } catch {
      return false;
    }
  }

  private async addContainer(container: TaggingContainer): Promise<void> {
    return this.tagCommander.addContainer(container.id, container.uri, container.node, [
      ['crossorigin', 'anonymous'],
    ]);
  }

  private throwIfNotInitialized() {
    if (!this.isInitialized) {
      throw new Error('Tagging is not initialized');
    }
  }
}
